import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { RefreshCw } from "lucide-react";
import { logActivity } from "@/services/gpsdrive-activity-log.service";

// Print 2026-10-09 (camada Mapa, "do nada arrastei e apareceu isso"):
// metade do mapa cinza com "Sentimos muito, mas não temos imagens daqui."
// Reproduzido aqui no servidor: basta UM pedido de tile falhar (rede
// oscilou) pro Google desenhar esse quadrado no lugar — e ele nunca tenta
// de novo, nem quando a internet volta, nem arrastando. Diferente do
// SatelliteZoomGuard (zoom acima da imagem de satélite, falta real de
// imagem): aqui a imagem existe, só o download falhou.
//
// No modo raster o quadrado de erro vira um <div> com essa frase dentro do
// mapa, então dá pra detectar pelo texto. Recuperação testada: descer 1
// zoom e voltar (moveCamera, sem animação) faz o Google pedir os tiles de
// novo. Também recarrega quando o navegador avisa que a rede voltou.
const NO_IMAGERY = /no imagery here|não temos imagens/i;
// Print 2026-10-21 (faixa cinza de novo, seguindo a CG 125): o zoom -1 e
// volta pede os MESMOS tiles e não bastou. Agora checa a cada 2 s e, a
// partir da 2ª tentativa no mesmo lugar, troca o tipo de mapa e volta — o
// Google descarta todos os tiles e redesenha do zero.
const CHECK_INTERVAL_MS = 2000;
const MIN_GAP_MS = 3000;
const MAX_GAP_MS = 12_000;
// Satélite/híbrido: se continuar cinza depois de N tentativas no mesmo
// lugar, é falta real de imagem — para até a câmera sair dali.
// Print 2026-10-20 (camada Mapa, seguindo o CAPTUR): a faixa cinza ficou
// pra sempre — a rede seguia falhando durante as 3 tentativas (o zoom -1
// também veio cinza) e a recuperação desistia; seguindo um carro devagar o
// "lugar" quase não muda. Em Ruas a imagem sempre existe: nunca desiste,
// só espaça as tentativas (8 s, 16 s, até 30 s).
const MAX_TRIES_SAME_SPOT = 3;
// 2026-10-23 (print com o mapa INTEIRO cinza, que não reproduz daqui — o
// Google entrega os tiles normalmente pro servidor): a partir da 4ª
// tentativa sem sucesso (~30 s) registra o episódio na aba LOGS do usuário
// (uma vez por episódio) e mostra um aviso com "Recarregar mapa".
const STUCK_AFTER_TRIES = 4;
// Print 2026-10-23 (mapa INTEIRO cinza seguindo a SPORTAGE, com uma grade
// de quadradinhos de erro minúsculos no meio): reproduzido aqui — os
// pedidos de ladrilho ficam TRAVADOS (nem carregam nem dão erro) e o zoom
// muda; o Google mantém os ladrilhos do zoom anterior encolhidos e nunca
// mais pede os do zoom atual, nem quando a rede volta. Sem a frase de erro
// no zoom atual, a checagem pelo texto não via nada. Agora, com o mapa
// parado há TILES_SETTLE_MS, também confere se o centro e 4 pontos em volta
// estão cobertos por um ladrilho carregado do tamanho normal (256 px).
// Só raster (no vetorial não existem <img> de ladrilho) e só depois de ver
// o mapa coberto uma vez nesta página — se o Google mudar o HTML dos
// ladrilhos, isto desliga sozinho em vez de ficar recarregando à toa.
const TILES_SETTLE_MS = 6000;
const TILE_PX = 256;
const TILE_SRC = /\/maps\/vt|\/vt\?|\/kh\?/;

function uncoveredPoints(map: google.maps.Map): number | null {
  if (map.getRenderingType?.() !== google.maps.RenderingType.RASTER) return null;
  const div = map.getDiv();
  const box = div.getBoundingClientRect();
  if (box.width < 50 || box.height < 50) return null;
  const tiles = [...div.querySelectorAll("img")]
    .filter((img) => TILE_SRC.test(img.src) && img.complete && img.naturalWidth > 0)
    .map((img) => img.getBoundingClientRect())
    .filter((r) => Math.abs(r.width - TILE_PX) < 2);
  const points = [
    [0.5, 0.5],
    [0.25, 0.25],
    [0.75, 0.25],
    [0.25, 0.75],
    [0.75, 0.75],
  ].map(([fx, fy]) => [box.left + box.width * fx, box.top + box.height * fy]);
  return points.filter(([x, y]) => !tiles.some((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom)).length;
}

function tileDiagnostics(map: google.maps.Map) {
  const c = map.getCenter();
  const since = performance.now() - 60_000;
  const tiles = (performance.getEntriesByType("resource") as PerformanceResourceTiming[]).filter(
    (e) => e.startTime > since && /\/maps\/vt|\/kh\?|\/vt\?/.test(e.name),
  );
  const statuses: Record<string, number> = {};
  for (const t of tiles) {
    const st = String((t as PerformanceResourceTiming & { responseStatus?: number }).responseStatus ?? "?");
    statuses[st] = (statuses[st] ?? 0) + 1;
  }
  const conn = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } }).connection;
  return {
    zoom: map.getZoom(),
    center: c ? `${c.lat().toFixed(5)},${c.lng().toFixed(5)}` : null,
    mapTypeId: map.getMapTypeId(),
    renderingType: map.getRenderingType?.(),
    online: navigator.onLine,
    connection: conn ? `${conn.effectiveType ?? "?"} ${conn.downlink ?? "?"}Mbps` : null,
    errorTiles: (map.getDiv().textContent?.match(/no imagery here|não temos imagens/gi) ?? []).length,
    uncoveredPoints: uncoveredPoints(map),
    tileRequests60s: tiles.length,
    tileStatuses: statuses,
    mapsVersion: (google.maps as unknown as { version?: string }).version ?? null,
    userAgent: navigator.userAgent,
  };
}

export function MapTileRecovery({ Mod }: { Mod: any }) {
  const map = Mod.useMap() as google.maps.Map | null;
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    let lastRefresh = 0;
    let tries = 0;
    let triesKey = "";
    let logged = false;
    let lastMove = Date.now();
    let sawCovered = false;
    let reason = "";

    const spotKey = () => {
      const c = map.getCenter();
      return c ? `${c.lat().toFixed(3)},${c.lng().toFixed(3)},${map.getZoom()}` : "";
    };

    const refresh = (force: boolean) => {
      const now = Date.now();
      const z = map.getZoom();
      const c = map.getCenter();
      if (z == null || !c) return;
      const key = spotKey();
      if (key !== triesKey) {
        triesKey = key;
        tries = 0;
      }
      const streets = map.getMapTypeId() === "roadmap";
      const gap = streets ? Math.min(MIN_GAP_MS * 2 ** Math.max(0, tries - 1), MAX_GAP_MS) : MIN_GAP_MS;
      if (!force && now - lastRefresh < gap) return;
      if (!force && !streets && tries >= MAX_TRIES_SAME_SPOT) return;
      tries += 1;
      lastRefresh = now;
      if (tries >= STUCK_AFTER_TRIES) {
        setStuck(true);
        if (!logged) {
          logged = true;
          logActivity({
            type: "ERROR",
            message: "Mapa: ladrilhos do Google não carregam (mapa cinza)",
            metadata: { kind: "map-tiles-stuck", reason, tries, ...tileDiagnostics(map) },
          });
        }
      }
      const type = map.getMapTypeId() ?? "roadmap";
      if (tries >= 2) {
        // Redesenho completo: outro tipo de mapa e volta.
        map.setMapTypeId(type === "terrain" ? "roadmap" : "terrain");
        setTimeout(() => map.setMapTypeId(type), 120);
      } else {
        map.moveCamera({ center: c, zoom: z - 1 });
        setTimeout(() => map.moveCamera({ center: c, zoom: z }), 80);
      }
    };

    const check = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      const div = map.getDiv();
      const missing = uncoveredPoints(map);
      if (missing === 0) sawCovered = true;
      const errorText = !!div && NO_IMAGERY.test(div.textContent ?? "");
      const grey = sawCovered && !!missing && Date.now() - lastMove > TILES_SETTLE_MS;
      if (errorText || grey) {
        reason = errorText ? "error-text" : "tiles-missing";
        refresh(false);
      } else {
        tries = 0; // recuperou: o próximo erro recomeça do intervalo curto
        logged = false;
        setStuck(false);
      }
    };

    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    const idle = map.addListener("idle", () => window.setTimeout(check, 1500));
    const moved = map.addListener("bounds_changed", () => {
      lastMove = Date.now();
    });
    const onOnline = () => refresh(true);
    // Aba volta a ficar visível: o check pulou enquanto estava escondida.
    const onVisible = () => {
      if (document.visibilityState === "visible") window.setTimeout(check, 500);
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      idle.remove();
      moved.remove();
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [map]);

  if (!stuck || typeof document === "undefined") return null;
  return createPortal(
    <div className="pointer-events-auto fixed left-1/2 top-3 z-[60] flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 shadow-lg dark:border-amber-500/40 dark:bg-amber-950 dark:text-amber-100">
      <span>O mapa não carregou algumas áreas.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 font-semibold text-white hover:bg-amber-600"
      >
        <RefreshCw className="h-3 w-3" /> Recarregar mapa
      </button>
    </div>,
    document.body,
  );
}
